'use client'

import throttle from 'lodash/throttle'
import { createContext, useCallback, useEffect, useState } from 'react'

import { configPublic } from '@src/config.public'
import { useInterval } from '@src/hooks/useInterval'
import { useSession } from '@src/hooks/useSession'
import { COOKIES, STORAGE } from '@src/utils/constants.util'
import { removeBrowserCookie, setBrowserCookie } from '@src/utils/cookies.util'
import { minutesToMilliseconds } from '@src/utils/date.util'
import { log } from '@src/utils/log.util'
import { storage } from '@src/utils/storage.util'

const inactivityTimeout = configPublic.general.inactivityTimeout
const inactivityCheckInterval = 5000

export interface ActivityMonitorContext {
  /** Whether the user is currently inactive */
  isInactive: boolean
  /** The time in minutes after which the user is considered inactive */
  inactivityTimeout: number
  /** The last time the user interacted with the app (ISO string) */
  lastActivityTimestamp: string | null
}

const activityMonitorContext = createContext<ActivityMonitorContext>({
  isInactive: false,
  inactivityTimeout,
  lastActivityTimestamp: null as string | null,
})

/**
 * Monitor user activity and log out the user if they are inactive for a certain amount of time.
 * Uses localStorage to sync the last activity timestamp between tabs.
 * Uses cookies to persist the last activity timestamp after the user closes the browser.
 */
export function ActivityMonitorProvider(props: { children: React.ReactNode }) {
  const [isInactive, setIsInactive] = useState(false)
  const [lastActivityTimestamp, setLastActivityTimestamp] = useState<string | null>(null)
  const { authSession, logout } = useSession()

  useEffect(() => {
    const handleActivity = throttle(function handleActivity() {
      if (document.visibilityState === 'hidden') return
      const timestamp = new Date().toISOString()
      setLastActivityTimestamp(timestamp)
      setBrowserCookie(COOKIES.lastActivityTimestamp, timestamp)
      storage.local.set(STORAGE.lastActivityTimestamp, timestamp)
      setIsInactive((isInactive) => {
        if (isInactive) log.debug('[ActivityMonitor] User is active again')
        return false
      })
    }, 1000)

    handleActivity()
    window.addEventListener('click', handleActivity)
    window.addEventListener('keydown', handleActivity)
    window.addEventListener('scroll', handleActivity)
    window.addEventListener('mousemove', handleActivity)
    window.addEventListener('touchstart', handleActivity)
    window.addEventListener('visibilitychange', handleActivity)

    return () => {
      window.removeEventListener('click', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('scroll', handleActivity)
      window.removeEventListener('mousemove', handleActivity)
      window.removeEventListener('touchstart', handleActivity)
      window.removeEventListener('visibilitychange', handleActivity)
    }
  }, [])

  useInterval(
    function checkInactivity() {
      const now = new Date().getTime()
      const lastActivityTimestamp = storage.local.get(STORAGE.lastActivityTimestamp)
      const lastActivityTime = new Date(`${lastActivityTimestamp}`).getTime() || now
      const shouldInactivate = now - lastActivityTime > minutesToMilliseconds(inactivityTimeout)
      if (shouldInactivate && !isInactive) {
        log.debug('[ActivityMonitor] User is inactive')
        handleInactivity()
      }
    },
    inactivityTimeout ? inactivityCheckInterval : null
  )

  const handleInactivity = useCallback(() => {
    setIsInactive(true)
    if (!authSession) return
    removeBrowserCookie(COOKIES.lastActivityTimestamp)
    storage.local.remove(STORAGE.lastActivityTimestamp)
    logout({ logoutReason: 'inactivity' })
  }, [authSession, logout])

  return (
    <activityMonitorContext.Provider
      value={{ isInactive, inactivityTimeout, lastActivityTimestamp }}
    >
      {props.children}
    </activityMonitorContext.Provider>
  )
}
